import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../firebase';
import { ref, onValue, push, serverTimestamp, set, query, orderByChild } from 'firebase/database';
import { useAuth } from '../../AuthContext';
import './Chat.css';

function Chat({ tripId, user, participant, onClose }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const { riderData } = useAuth();
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (!tripId) return;

    const chatQuery = query(ref(db, `trips/${tripId}/chat`), orderByChild('timestamp'));
    const unsubscribe = onValue(chatQuery, (snapshot) => {
      const chatData = snapshot.val();
      const loadedMessages = [];
      for (const key in chatData) {
        loadedMessages.push({ id: key, ...chatData[key] });
      }
      setMessages(loadedMessages);
    });

    return () => unsubscribe();
  }, [tripId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = (text) => {
    if (text.trim() === '' || !tripId || !user) return;

    const chatRef = ref(db, `trips/${tripId}/chat`);
    const newMessageRef = push(chatRef);
    
    const riderName = ((riderData?.first_name || '') + ' ' + (riderData?.last_name || '')).trim() || 'Rider';

    set(newMessageRef, {
      text: text.trim(),
      senderId: user.uid,
      senderName: riderName,
      timestamp: serverTimestamp(),
    });
  };

  const handleSendMessage = () => {
    sendMessage(newMessage);
    setNewMessage('');
  };

  const suggestions = ["I'm on my way", "I've arrived", "Okay", "Where are you?"];

  return (
    <div className="chat-container">
      <div className="chat-header">
        <div className="chat-participant-info">
          <img src={participant.photoUrl} alt={participant.name} className="chat-participant-photo" />
          <div className="chat-participant-details">
            <h3>{participant.name}</h3>
            <span>Driver</span>
          </div>
        </div>
        <button onClick={onClose} className="chat-close-btn">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>
      </div>
      <div className="chat-body">
        {messages.map((msg) => (
          <div key={msg.id} className={`chat-message-wrapper ${msg.senderId === user.uid ? 'sent' : 'received'}`}>
            <div className="chat-message">
              {msg.text}
              <div className="chat-timestamp">
                {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      <div className="chat-suggestions">
        {suggestions.map((text) => (
          <button key={text} className="suggestion-pill" onClick={() => sendMessage(text)}>
            {text}
          </button>
        ))}
      </div>
      <div className="chat-footer">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
          placeholder="Type a message..."
        />
        <button onClick={handleSendMessage} disabled={!newMessage.trim()}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"></path></svg>
        </button>
      </div>
    </div>
  );
}

export default Chat;